import { useCallback, useEffect, useMemo, useState } from "react";
import Page from "../Components/Page";
import AggregatorSelectedGrowerPanel, {
  AggregatorScopeWarning,
  useSelectedAggregatorGrower,
} from "../Components/AggregatorSelectedGrowerPanel.jsx";
import {
  GrowerEmptyState,
  GrowerSectionHeading,
  GrowerSurface,
} from "../Components/GrowerLayout.jsx";
import { axiosInstance } from "../Components/axiosInstance.js";
import {
  RECONCILIATION_ACTIONS,
  formatExcelFieldValue,
} from "../utils/excelExchange.js";
import { trackEvent } from "../analytics/ga4.js";
import {
  RECONCILIATION_WRITE_SCOPE,
  selectedGrowerHasScope,
} from "../portal/aggregatorGrowerContext.js";

function unwrapRows(payload) {
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  return [];
}

function statusTone(status) {
  return status === "OPEN"
    ? "border-[#d8a31a] bg-[#fff8e5] text-[#7a5200]"
    : "border-[#34a853]/40 bg-[#eef8f1] text-[#1d6f38]";
}

function caseLabel(item) {
  return String(item || "")
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function ReconciliationActionButton({
  children,
  disabled,
  onClick,
  tone = "primary",
}) {
  const toneClass =
    tone === "secondary"
      ? "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
      : tone === "danger"
        ? "border-[#c0262d] bg-white text-[#9f1239] hover:bg-[#fff1f2]"
        : "border-[#f15d22] bg-[#f15d22] text-white hover:bg-[#d94818]";

  return (
    <button
      className={`inline-flex min-h-[38px] items-center justify-center rounded-[4px] border px-3 py-2 text-sm font-bold transition disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400 ${toneClass}`}
      disabled={disabled}
      onClick={onClick}
      type="button"
    >
      {children}
    </button>
  );
}

function ReconciliationItem({ item, casePublicId, isResolving, onResolve }) {
  return (
    <div className="rounded-[8px] border border-slate-200 bg-white p-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <p className="text-[12px] font-bold uppercase tracking-[0.12em] text-slate-500">
            {item.field_name || "Record"}
          </p>
          <p className="mt-1 text-sm font-semibold text-slate-800">
            {caseLabel(item.target_record_type)}{" "}
            {item.target_record_public_id ? (
              <span className="text-slate-500">{item.target_record_public_id}</span>
            ) : null}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ReconciliationActionButton
            disabled={isResolving}
            onClick={() =>
              onResolve(casePublicId, RECONCILIATION_ACTIONS.update, item.id)
            }
          >
            Accept
          </ReconciliationActionButton>
          <ReconciliationActionButton
            disabled={isResolving}
            onClick={() =>
              onResolve(casePublicId, RECONCILIATION_ACTIONS.override, null, item.target_record_public_id)
            }
            tone="secondary"
          >
            Accept Record
          </ReconciliationActionButton>
          <ReconciliationActionButton
            disabled={isResolving}
            onClick={() =>
              onResolve(casePublicId, RECONCILIATION_ACTIONS.discard, item.id)
            }
            tone="danger"
          >
            Keep Current
          </ReconciliationActionButton>
        </div>
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <div className="rounded-[8px] border border-slate-200 bg-slate-50 p-3">
          <p className="text-[12px] font-bold uppercase tracking-[0.12em] text-slate-500">
            Current
          </p>
          <p className="mt-2 break-words text-sm text-slate-800">
            {formatExcelFieldValue(item.field_name, item.current_value)}
          </p>
        </div>
        <div className="rounded-[8px] border border-[#d8a31a]/40 bg-[#fff8e5] p-3">
          <p className="text-[12px] font-bold uppercase tracking-[0.12em] text-[#7a5200]">
            Incoming
          </p>
          <p className="mt-2 break-words text-sm text-slate-900">
            {formatExcelFieldValue(item.field_name, item.incoming_value)}
          </p>
        </div>
      </div>
    </div>
  );
}

function ReconciliationCase({ item, resolvingKey, onResolve }) {
  const openItems = (item.items || []).filter((row) => row.status !== "RESOLVED");

  return (
    <div className="rounded-[8px] border border-slate-200 bg-white shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
      <div className="flex flex-col gap-4 border-b border-slate-200 bg-[#fcfcfa] px-5 py-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`rounded-full border px-2.5 py-1 text-[12px] font-bold uppercase tracking-[0.08em] ${statusTone(item.status)}`}
            >
              {caseLabel(item.status)}
            </span>
            <span className="text-sm font-semibold text-slate-500">
              {caseLabel(item.case_type)}
            </span>
          </div>
          <h3 className="mt-2 text-xl font-black text-slate-900">
            {item.public_id}
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            {openItems.length} field{openItems.length === 1 ? "" : "s"} need review
          </p>
        </div>
        <ReconciliationActionButton
          disabled={Boolean(resolvingKey)}
          onClick={() =>
            onResolve(item.public_id, RECONCILIATION_ACTIONS.override)
          }
          tone="secondary"
        >
          Accept All
        </ReconciliationActionButton>
      </div>
      <div className="space-y-3 p-5">
        {openItems.map((row) => (
          <ReconciliationItem
            casePublicId={item.public_id}
            isResolving={resolvingKey === `${item.public_id}:${row.id}`}
            item={row}
            key={row.id}
            onResolve={onResolve}
          />
        ))}
      </div>
    </div>
  );
}

export default function AggregatorReconciliationPage() {
  const selectedGrower = useSelectedAggregatorGrower();
  const selectedGrowerPublicId = selectedGrower?.grower_profile_public_id || "";
  const canResolveForSelectedGrower = selectedGrowerHasScope(
    selectedGrower,
    RECONCILIATION_WRITE_SCOPE
  );
  const [cases, setCases] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [resolvingKey, setResolvingKey] = useState("");

  const loadCases = useCallback(async () => {
    if (!selectedGrowerPublicId || !canResolveForSelectedGrower) {
      setCases([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setErrorMessage("");
    try {
      const response = await axiosInstance.get("/reconciliation/cases");
      setCases(unwrapRows(response.data));
    } catch (error) {
      console.error("Failed to load reconciliation cases", error);
      setCases([]);
      setErrorMessage(
        error?.response?.data?.detail ||
          "Reconciliation cases could not be loaded."
      );
    } finally {
      setIsLoading(false);
    }
  }, [canResolveForSelectedGrower, selectedGrowerPublicId]);

  useEffect(() => {
    loadCases();
  }, [loadCases]);

  const openCases = useMemo(
    () => cases.filter((item) => item.status === "OPEN"),
    [cases]
  );

  async function resolveCase(casePublicId, action, itemId = null, targetRecordPublicId = null) {
    const key = `${casePublicId}:${itemId || targetRecordPublicId || "all"}`;
    setResolvingKey(key);
    setErrorMessage("");
    setStatusMessage("");

    try {
      await axiosInstance.post(`/reconciliation/cases/${casePublicId}/resolve`, {
        action,
        item_id: itemId,
        target_record_public_id: targetRecordPublicId,
      });
      setStatusMessage("Reconciliation updated.");
      trackEvent("aggregator_reconciliation_resolved", {
        workflow_area: "reconciliation",
        resolution_action: action,
      });
      await loadCases();
    } catch (error) {
      console.error("Failed to resolve reconciliation case", error);
      setErrorMessage(
        error?.response?.data?.detail ||
          "The reconciliation case could not be resolved."
      );
    } finally {
      setResolvingKey("");
    }
  }

  return (
    <Page
      title="Reconciliation"
      headerVariant="hybrid"
      headerBorderColor="border-[#d8a31a]"
      contentWidth="wide"
      padding="px-4 py-5 md:px-8 md:py-7"
      introContent={
        <span>
          Review import conflicts for the selected grower and choose which values
          should become the source of truth.
        </span>
      }
    >
      <div className="w-full space-y-5 text-[#5c6673]">
        <AggregatorSelectedGrowerPanel selectedGrower={selectedGrower} />

        {!selectedGrowerPublicId ? null : (
          <>
            {!canResolveForSelectedGrower ? (
              <AggregatorScopeWarning
                message="This selected grower link cannot open or resolve reconciliation cases."
                requiredScope={RECONCILIATION_WRITE_SCOPE}
              />
            ) : null}
            {!canResolveForSelectedGrower ? null : (
              <>
            {errorMessage ? (
              <div className="rounded-[6px] border border-[#f2b8b5] bg-[#fff1f0] px-4 py-3 text-[#9b1c13]">
                {errorMessage}
              </div>
            ) : null}
            {statusMessage ? (
              <div className="rounded-[6px] border border-[#a7d7b7] bg-[#eef8f0] px-4 py-3 text-[#1f6d38]">
                {statusMessage}
              </div>
            ) : null}

            <GrowerSurface>
              <GrowerSectionHeading
                eyebrow="Conflict Queue"
                title="Resolve records that need a human decision"
                description="Most imported rows apply automatically. This queue only shows fields where incoming data disagrees with an existing record."
              />
              <div className="mt-5 grid gap-3 md:grid-cols-3">
                <div className="rounded-[8px] border border-slate-200 bg-white px-4 py-3">
                  <p className="text-[12px] font-bold uppercase tracking-[0.14em] text-slate-500">
                    Open Cases
                  </p>
                  <p className="mt-1 text-2xl font-black text-slate-900">
                    {openCases.length}
                  </p>
                </div>
                <div className="rounded-[8px] border border-slate-200 bg-white px-4 py-3">
                  <p className="text-[12px] font-bold uppercase tracking-[0.14em] text-slate-500">
                    Open Fields
                  </p>
                  <p className="mt-1 text-2xl font-black text-slate-900">
                    {openCases.reduce(
                      (count, item) =>
                        count +
                        (item.items || []).filter((row) => row.status !== "RESOLVED").length,
                      0
                    )}
                  </p>
                </div>
                <div className="rounded-[8px] border border-slate-200 bg-white px-4 py-3">
                  <p className="text-[12px] font-bold uppercase tracking-[0.14em] text-slate-500">
                    Source
                  </p>
                  <p className="mt-1 text-2xl font-black text-slate-900">
                    Excel
                  </p>
                </div>
              </div>
            </GrowerSurface>

            {isLoading ? (
              <div className="rounded-[8px] border border-slate-200 bg-white p-5">
                Loading reconciliation cases...
              </div>
            ) : openCases.length === 0 ? (
              <GrowerEmptyState
                title="No Open Conflicts"
                description="This grower has no reconciliation cases waiting for review."
              />
            ) : (
              <div className="space-y-4">
                {openCases.map((item) => (
                  <ReconciliationCase
                    item={item}
                    key={item.public_id}
                    onResolve={resolveCase}
                    resolvingKey={resolvingKey}
                  />
                ))}
              </div>
            )}
              </>
            )}
          </>
        )}
      </div>
    </Page>
  );
}
